// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 zouwei

/**
 * Pure geometry for the board scene: bounding boxes, hit testing and
 * marquee (rectangle) selection. No DOM, no layout reads — safe to call
 * from a host's pointer-move handler at 60fps (perf rule: pointer callbacks
 * do zero layout work).
 *
 * Rotation handling: an element's `rotation` is around its bbox center. Hit
 * testing un-rotates the query point into the element's local frame, then
 * tests against the axis-aligned box — so callers never rotate geometry,
 * only points.
 */

import type {
  BoardElement,
  FormulaElement,
  ShapeElement,
  StrokeElement,
  TextElement,
} from './types'

export interface Point {
  x: number
  y: number
}

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

/** Fallback natural size for an unmeasured text/formula element. */
function estimatedSize(el: TextElement | FormulaElement): {
  width: number
  height: number
} {
  if (el.measured) return el.measured
  const lines = el.type === 'text' ? el.text.split('\n') : ['']
  const cols = Math.max(1, ...lines.map((l) => l.length))
  // Rough monospace-ish estimate; only used until the host measures.
  return { width: cols * el.fontSize * 0.6, height: lines.length * el.fontSize * 1.3 }
}

/** Axis-aligned bounding box in scene coordinates (ignores rotation). */
/**
 * Absolute vertices of a linear shape (line/arrow). Defaults to the two-point
 * segment (x,y)→(x+width,y+height) when the element has no explicit `points`.
 */
export function linearPoints(el: ShapeElement): Point[] {
  if (el.points && el.points.length >= 2) {
    return el.points.map(([px, py]) => ({ x: el.x + px, y: el.y + py }))
  }
  return [
    { x: el.x, y: el.y },
    { x: el.x + el.width, y: el.y + el.height },
  ]
}

export function elementBBox(el: BoardElement): Rect {
  switch (el.type) {
    case 'stroke': {
      if (el.points.length === 0) return { x: el.x, y: el.y, width: 0, height: 0 }
      let minX = Infinity
      let minY = Infinity
      let maxX = -Infinity
      let maxY = -Infinity
      for (const [px, py] of el.points) {
        if (px < minX) minX = px
        if (px > maxX) maxX = px
        if (py < minY) minY = py
        if (py > maxY) maxY = py
      }
      const pad = el.size / 2
      return {
        x: el.x + minX - pad,
        y: el.y + minY - pad,
        width: maxX - minX + el.size,
        height: maxY - minY + el.size,
      }
    }
    case 'shape': {
      // Multi-point line/arrow: normalized bbox over all vertices.
      if ((el.kind === 'line' || el.kind === 'arrow') && el.points && el.points.length >= 2) {
        const pts = linearPoints(el)
        let minX = Infinity
        let minY = Infinity
        let maxX = -Infinity
        let maxY = -Infinity
        for (const { x, y } of pts) {
          if (x < minX) minX = x
          if (x > maxX) maxX = x
          if (y < minY) minY = y
          if (y > maxY) maxY = y
        }
        return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
      }
      return { x: el.x, y: el.y, width: el.width, height: el.height }
    }
    case 'image':
    case 'video':
    case 'audio':
    case 'frame':
    case 'custom':
      return { x: el.x, y: el.y, width: el.width, height: el.height }
    case 'text':
    case 'formula': {
      const { width, height } = estimatedSize(el)
      return { x: el.x, y: el.y, width, height }
    }
  }
}

export function rectCenter(r: Rect): Point {
  return { x: r.x + r.width / 2, y: r.y + r.height / 2 }
}

/** Rotate `p` by `rad` around `center`. */
export function rotatePoint(p: Point, center: Point, rad: number): Point {
  if (rad === 0) return p
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const dx = p.x - center.x
  const dy = p.y - center.y
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  }
}

/** Squared distance from `p` to segment `a`–`b` (avoids a sqrt in the loop). */
function distToSegmentSq(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lenSq = dx * dx + dy * dy
  let t = lenSq === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  const cx = a.x + t * dx
  const cy = a.y + t * dy
  return (p.x - cx) ** 2 + (p.y - cy) ** 2
}

function pointInRect(p: Point, r: Rect): boolean {
  return p.x >= r.x && p.x <= r.x + r.width && p.y >= r.y && p.y <= r.y + r.height
}

/** Query point mapped into the element's un-rotated local frame. */
function toLocal(el: BoardElement, p: Point): Point {
  if (!el.rotation) return p
  return rotatePoint(p, rectCenter(elementBBox(el)), -el.rotation)
}

function hitStroke(el: StrokeElement, local: Point, tolerance: number): boolean {
  const bbox = elementBBox(el)
  // Coarse bbox reject (expanded by tolerance) before per-segment work.
  if (
    !pointInRect(local, {
      x: bbox.x - tolerance,
      y: bbox.y - tolerance,
      width: bbox.width + tolerance * 2,
      height: bbox.height + tolerance * 2,
    })
  ) {
    return false
  }
  const threshold = (el.size / 2 + tolerance) ** 2
  // Subsample very long strokes so a fat stroke stays cheap to test.
  const step = el.points.length > 512 ? 2 : 1
  for (let i = step; i < el.points.length; i += step) {
    const a = { x: el.x + el.points[i - step]![0], y: el.y + el.points[i - step]![1] }
    const b = { x: el.x + el.points[i]![0], y: el.y + el.points[i]![1] }
    if (distToSegmentSq(local, a, b) <= threshold) return true
  }
  // Single-point stroke (a dot): distance to that point.
  if (el.points.length === 1) {
    const only = el.points[0]!
    const d = (local.x - (el.x + only[0])) ** 2 + (local.y - (el.y + only[1])) ** 2
    return d <= threshold
  }
  return false
}

/**
 * True if point `p` (scene coords) hits element `el` within `tolerance`
 * scene units. Strokes use nearest-segment distance; unfilled shapes use a
 * stroked-outline band; filled shapes, text, formula and image use the bbox.
 */
export function hitTest(el: BoardElement, p: Point, tolerance = 4): boolean {
  const local = toLocal(el, p)
  if (el.type === 'stroke') return hitStroke(el, local, tolerance)

  const bbox = elementBBox(el)
  if (el.type === 'frame') {
    // Selectable by its border band or its label tab only — clicks inside the
    // frame fall through to the content drawn on top of it.
    const band = 1.5 / 2 + tolerance
    const label = pointInRect(local, { x: bbox.x, y: bbox.y - 20, width: Math.min(bbox.width, 160), height: 20 })
    if (label) return true
    const outer = pointInRect(local, { x: bbox.x - band, y: bbox.y - band, width: bbox.width + band * 2, height: bbox.height + band * 2 })
    if (!outer) return false
    const inner = pointInRect(local, {
      x: bbox.x + band,
      y: bbox.y + band,
      width: Math.max(0, bbox.width - band * 2),
      height: Math.max(0, bbox.height - band * 2),
    })
    return !inner
  }
  if (el.type === 'shape' && el.fill === undefined) {
    // Outline-only hit: inside the outer band but outside the inner rect.
    const band = el.strokeWidth / 2 + tolerance
    const outer = pointInRect(local, {
      x: bbox.x - band,
      y: bbox.y - band,
      width: bbox.width + band * 2,
      height: bbox.height + band * 2,
    })
    if (!outer) return false
    if (el.kind === 'line' || el.kind === 'arrow') {
      // Nearest of the polyline's segments (two-point line falls out of this).
      const pts = linearPoints(el)
      for (let i = 0; i < pts.length - 1; i++) {
        if (distToSegmentSq(local, pts[i]!, pts[i + 1]!) <= band * band) return true
      }
      return false
    }
    const innerHit = pointInRect(local, {
      x: bbox.x + band,
      y: bbox.y + band,
      width: Math.max(0, bbox.width - band * 2),
      height: Math.max(0, bbox.height - band * 2),
    })
    return !innerHit
  }
  return pointInRect(local, {
    x: bbox.x - tolerance,
    y: bbox.y - tolerance,
    width: bbox.width + tolerance * 2,
    height: bbox.height + tolerance * 2,
  })
}

/** Rectangle intersection test (AABB). */
export function rectsIntersect(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  )
}

/** True if a marquee `r` selects element `el` (bbox intersection). */
export function rectHits(el: BoardElement, r: Rect): boolean {
  return rectsIntersect(elementBBox(el), r)
}

/** Union bbox of several elements (e.g. a multi-selection). Null if empty. */
export function unionBBox(elements: BoardElement[]): Rect | null {
  if (elements.length === 0) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const el of elements) {
    const b = elementBBox(el)
    if (b.x < minX) minX = b.x
    if (b.y < minY) minY = b.y
    if (b.x + b.width > maxX) maxX = b.x + b.width
    if (b.y + b.height > maxY) maxY = b.y + b.height
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}
